import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, KeyRound } from 'lucide-react';

/**
 * Shows a forced password change modal whenever the logged-in user
 * has `force_password_change` flag set (newly created accounts).
 * Modal cannot be closed until password is changed.
 */
const ForcePasswordChange = () => {
  const { user, token, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState({ next: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !token) { setOpen(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/auth/me');
        if (cancelled) return;
        if (res.data?.force_password_change) setOpen(true);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  const submit = async () => {
    if (pwd.next.length < 6) { toast.error('Min 6 characters'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/me/change-password', { new_password: pwd.next });
      toast.success('Password changed. Please log in again.');
      setOpen(false);
      setTimeout(() => logout(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* not closable */ }}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="force-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound size={20} />Set a new password</DialogTitle>
          <DialogDescription>
            For security, you must change the temporary password before continuing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>New Password</Label><Input type="password" value={pwd.next} onChange={(e) => setPwd({...pwd, next: e.target.value})} data-testid="force-new-pwd" /></div>
          <div><Label>Confirm Password</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({...pwd, confirm: e.target.value})} data-testid="force-confirm-pwd" /></div>
          <Button onClick={submit} disabled={busy} className="w-full" data-testid="force-submit-pwd">
            {busy && <Loader2 size={14} className="animate-spin mr-1" />}Update Password
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChange;
